import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import {
  FormControl,
  FormHelperText,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { API_endspoints } from "../../API/API_endspoints";
import { API_GET, API_GET2, API_POST } from "../../API/config";
import { toast } from "react-toastify";
import DatePickerComponent from "../../Common/DatePicker";
import moment from "moment/moment";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "100%",
  maxWidth: "600px",
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  maxHeight: "90vh",
  overflow: "auto",
};

const TYPES_OF_DISCOUNT = [
  { id: 1, label: "Percentage Discount", value: 1 },
  { id: 2, label: "Fixed Amount", value: 2 },
];

// const SUBSCRIPTION_TYPES = [
//   { id: 3, label: "All Subscriptions", value: 3 },
//   { id: 1, label: "Monthly Only", value: 1 },
//   { id: 2, label: "Yearly Only", value: 2 },
// ];

export default function AddNewCouponModal({
  getData,
  open,
  setOpen,
  setModalData,
  modalData,
}) {
  const handleOpen = () => {
    setOpen(true);
    setModalData({});
  };
  const handleClose = () => setOpen(false);
  const [codePrefix, setCodePrefix] = React.useState("");
  const [loading, setloading] = React.useState(false);
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [subScriptionType, setSubScriptionType] = React.useState([]);

  const getSubscriptionType = async () => {
    try {
      const res = await API_GET2(API_endspoints.getSubscriptionType);
      if (res?.s) {
        const updatedData = res?.r
          ?.filter((item) => item.is_trial === 0)
          .map((item) => ({
            ...item,
            label: `${item?.name} (${
              item?.month === 1 ? "Monthly" : "Annually"
            })`,
            value: item?.id,
          }));
        const all = { id: -1, label: "All", value: -1 };
        setSubScriptionType([all, ...updatedData]);
      }
    } catch (error) {
      console.log(error);
    }
  };

  React.useEffect(() => {
    getSubscriptionType();
  }, []);

  React.useEffect(() => {
    const isEdit = modalData?.id ? true : false;
    setIsEditMode(isEdit);
  }, [modalData]);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Enhanced validation schema with proper date handling
  const validationSchema = yup.object({
    coupon_code: yup
      .string()
      .trim()
      .required("Coupon code is required")
      .matches(
        /^[A-Za-z0-9-]+$/,
        "Only alphanumeric characters and hyphens are allowed"
      )
      .max(20, "Coupon code must be 20 characters or less"),
    coupon_name: yup
      .string()
      .trim()
      .required("Coupon name is required")
      .max(50, "Coupon name must be 50 characters or less"),
    description: yup
      .string()
      .trim()
      .required("Description is required")
      .max(255, "Description must be 255 characters or less"),
    discount_type: yup
      .number()
      .required("Discount type is required")
      .oneOf([1, 2], "Invalid discount type"),
    discount_value: yup
      .number()
      .required("Discount value is required")
      .test(
        "valid-percentage",
        "Percentage must be between 1-100%",
        function (value) {
          const { discount_type } = this.parent;
          if (discount_type === 1 && (value < 1 || value > 100)) {
            return false;
          }
          return true;
        }
      )
      .test(
        "valid-amount",
        "Fixed amount must be greater than 0",
        function (value) {
          const { discount_type } = this.parent;
          if (discount_type === 2 && value <= 0) {
            return false;
          }
          return true;
        }
      )
      .positive("Must be a positive number"),
    total_usage_limit: yup
      .number()
      .required("Total usage limit is required")
      .integer("Must be a whole number")
      .min(1, "Must be at least 1"),
    limit_per_person: yup
      .number()
      .required("Limit per customer is required")
      .integer("Must be a whole number")
      .min(1, "Please enter valid limit"),
    start_date: yup
      .date()
      .required("Start date is required")
      .test(
        "is-today-or-future",
        "Start date must be today or in the future",
        function (value) {
          return value
            ? moment(value)
                .startOf("day")
                .isSameOrAfter(moment().startOf("day"))
            : false;
        }
      ),
    end_date: yup
      .date()
      .required("End date is required")
      .test(
        "is-same-or-after-start",
        "End date must be same or after start date",
        function (value) {
          const { start_date } = this.parent;
          return value && start_date
            ? moment(value)
                .startOf("day")
                .isSameOrAfter(moment(start_date).startOf("day"))
            : false;
        }
      ),
    subscription_type: yup
      .number()
      .required("Subscription type is required")
      .oneOf([-1,1,2,3,4,5,6], "Invalid subscription type"),
  });

  const formik = useFormik({
    initialValues: {
      coupon_code: modalData?.coupon_code || "",
      coupon_name: modalData?.coupon_name || "",
      description: modalData?.description || "",
      discount_type: modalData?.discount_type || 1,
      discount_value: modalData?.discount_amount || 0,
      start_date: modalData?.start_date || "",
      end_date: modalData?.end_date || "",
      total_usage_limit: modalData?.total_usage_limit || 0,
      limit_per_person: modalData?.usage_limit_per_user || 1,
      subscription_type: modalData?.subscription_type || -1,
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      setloading(true);
      try {
        const formattedStartDate = moment
          .utc(values.start_date)
          .format("YYYY-MM-DD HH:mm:ss");
        const formattedEndDate = moment
          .utc(values.end_date)
          .endOf("day")
          .format("YYYY-MM-DD HH:mm:ss");
        const formdata = new FormData();
        formdata.append("subscription_type", values.subscription_type);
        !isEditMode && formdata.append("coupon_code", values.coupon_code);
        formdata.append("coupon_name", values.coupon_name);
        formdata.append("description", values.description);
        formdata.append("discount_type", values.discount_type);
        formdata.append("discount_amount", values.discount_value);
        formdata.append("start_date", formattedStartDate);
        formdata.append("end_date", formattedEndDate);
        formdata.append("total_usage_limit", values.total_usage_limit);
        formdata.append("usage_limit_per_user", values.limit_per_person);

        if (modalData?.id) {
          formdata.append("id", modalData?.id);
        }

        const api = `${
          modalData?.id
            ? API_endspoints.UpdateDiscountCoupon
            : API_endspoints.addDiscountCoupon
        }`;
        let res = await API_POST(api, formdata);
        if (res?.s) {
          toast.success(
            `Coupon ${modalData?.id ? "updated" : "created"} successfully`
          );
          getData();
          handleClose();
        } else {
          toast.warn(res?.m);
          setModalData({});
        }
        setloading(false);
      } catch (error) {
        console.log(error);
        setloading(false);
        setSubmitting(false);
        toast.error("Failed to create coupon");
        handleClose();
      }
    },
  });

  // const formatDateForAPI = (date) => {
  //   return date ? moment(date).format("YYYY-MM-DD") : "";
  // };

  const generateCouponCode = () => {
    const randomCode = Math.random().toString(36).substring(2, 8).toUpperCase();
    const prefix =
      codePrefix.trim() !== "" ? codePrefix.toUpperCase() + "-" : "";
    return prefix + randomCode;
  };

  const textFieldStyle = {
    margin: "0.5rem 0",
    ".MuiInputBase-root": {
      borderRadius: "11px",
    },
    ".MuiOutlinedInput-input:focus": {
      outline: "none",
    },
  };

  const inputProps = {
    sx: {
      borderRadius: "12px",
      color: "#000",
      "&:hover": {
        color: "#F57C00",
        bgcolor: "#FFF3E8",
        borderRadius: "12px",
      },
    },
  };

  React.useEffect(() => {
    if (open) {
      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);

      formik.setFieldValue("start_date", today);
      formik.setFieldValue("end_date", tomorrow);
    }
  }, [open]);

  return (
    <div>
      <Button
        onClick={handleOpen}
        variant="contained"
        sx={{
          width: "100px",
          textTransform: "none",
          borderRadius: "8px",
          height: 45,
          backgroundColor: "#164FA1",
          "&:hover": {
            backgroundColor: "#164FA1",
          },
        }}
      >
        Add
      </Button>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            {modalData?.id ? "Update Coupon" : "Add New Coupon"}
          </Typography>
          <Box sx={{ width: "100%", mt: 2 }}>
            <Box>
              Coupon Code <span style={{ color: "red" }}>*</span>
            </Box>
            <TextField
              type="text"
              id="coupon_code"
              name="coupon_code"
              disabled={isEditMode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.coupon_code}
              error={
                formik.touched.coupon_code && Boolean(formik.errors.coupon_code)
              }
              helperText={
                formik.touched.coupon_code && formik.errors.coupon_code
              }
              variant="outlined"
              fullWidth
              InputProps={{
                ...inputProps,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Generate Coupon Code">
                      <Button
                        onClick={() => {
                          formik.setFieldValue(
                            "coupon_code",
                            generateCouponCode()
                          );
                        }}
                        disabled={isEditMode}
                        variant="contained"
                        size="small"
                        sx={{
                          textTransform: "none",
                          borderRadius: "8px",
                          backgroundColor: "#164FA1",
                          "&:hover": {
                            backgroundColor: "#164FA1",
                          },
                        }}
                      >
                        Generate
                      </Button>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              sx={textFieldStyle}
              placeholder="e.g. SUMMER20"
            />
          </Box>
          <Box sx={{ width: "100%" }}>
            <Box>
              Coupon Name <span style={{ color: "red" }}>*</span>
            </Box>
            <TextField
              type="text"
              id="coupon_name"
              name="coupon_name"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.coupon_name}
              error={
                formik.touched.coupon_name && Boolean(formik.errors.coupon_name)
              }
              helperText={
                formik.touched.coupon_name && formik.errors.coupon_name
              }
              variant="outlined"
              fullWidth
              InputProps={inputProps}
              sx={textFieldStyle}
              placeholder="Summer sale 2025"
            />
          </Box>
          <Box sx={{ width: "100%" }}>
            <Box>
              Description <span style={{ color: "red" }}>*</span>
            </Box>
            <TextField
              type="text"
              id="description"
              name="description"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.description}
              error={
                formik.touched.description && Boolean(formik.errors.description)
              }
              helperText={
                formik.touched.description && formik.errors.description
              }
              variant="outlined"
              fullWidth
              InputProps={inputProps}
              sx={textFieldStyle}
              placeholder="Get 20% off during our Summer sale."
            />
          </Box>
          <Box
            sx={{ display: "flex", gap: 2, justifyContent: "space-between" }}
          >
            <Box sx={{ width: "100%" }}>
              <Box>
                Discount Type <span style={{ color: "red" }}>*</span>
              </Box>
              <Select
                value={formik.values.discount_type}
                id="discount_type"
                name="discount_type"
                onChange={(e) => {
                  formik.setFieldValue("discount_type", e.target.value);
                  // Reset discount value when changing type
                  formik.setFieldValue("discount_value", 0);
                }}
                size="small"
                sx={{
                  margin: "0.5rem 0",
                  minWidth: 150,
                  height: 55,
                  width: "100%",
                  padding: "4px 12px",
                  fontSize: "14px",
                  borderRadius: "12px",
                  "&:hover": {
                    backgroundColor: "#FFF3E8",
                    borderRadius: "12px",
                    color: "#F57C00",
                  },
                }}
              >
                {TYPES_OF_DISCOUNT.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.discount_type && formik.errors.discount_type && (
                <FormHelperText error>
                  {formik.errors.discount_type}
                </FormHelperText>
              )}
            </Box>
            <Box sx={{ width: "100%" }}>
              <Box>
                Discount Value <span style={{ color: "red" }}>*</span>
              </Box>
              <TextField
                type="number"
                id="discount_value"
                name="discount_value"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.discount_value}
                error={
                  formik.touched.discount_value &&
                  Boolean(formik.errors.discount_value)
                }
                helperText={
                  formik.touched.discount_value && formik.errors.discount_value
                }
                variant="outlined"
                fullWidth
                InputProps={{
                  ...inputProps,
                  endAdornment: (
                    <InputAdornment position="end">
                      {formik.values.discount_type === 1 ? "%" : "€"}
                    </InputAdornment>
                  ),
                }}
                sx={textFieldStyle}
                placeholder={
                  formik.values.discount_type === 1
                    ? "e.g. 20 (for 20%)"
                    : "e.g. 10.00"
                }
              />
            </Box>
          </Box>

          {/* Date Range */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "space-between",
              mt: 2,
            }}
          >
            <Box sx={{ width: "100%" }}>
              <Box>
                Start Date <span style={{ color: "red" }}>*</span>
              </Box>
              <DatePickerComponent
                disablePast={true}
                formik={formik}
                name={"start_date"}
                // error={
                //     formik.touched.start_date && Boolean(formik.errors.start_date)
                // }
                // helperText={
                //     formik.touched.start_date && formik.errors.start_date
                // }
              />
              {formik.touched.start_date && formik.errors.start_date && (
                <div
                  style={{
                    color: "red",
                    fontSize: "12px",
                    textAlign: "start",
                  }}
                >
                  {formik.errors.start_date}
                </div>
              )}
            </Box>
            <Box sx={{ width: "100%" }}>
              <Box>
                End Date <span style={{ color: "red" }}>*</span>
              </Box>
              <DatePickerComponent
                disablePast={true}
                // minDate={formik.values.start_date || new Date()}
                formik={formik}
                name={"end_date"}
                // error={
                //     Boolean(formik.errors.end_date)
                // }
                // helperText={formik.errors.end_date}
              />
              {formik.touched.end_date && formik.errors.end_date && (
                <div
                  style={{
                    color: "red",
                    fontSize: "12px",
                    textAlign: "start",
                  }}
                >
                  {formik.errors.end_date}
                </div>
              )}
            </Box>
          </Box>

          {/* Usage Limits */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "space-between",
              mt: 2,
            }}
          >
            <Box sx={{ width: "100%" }}>
              <Box>
                Total Usage Limit <span style={{ color: "red" }}>*</span>
              </Box>
              <TextField
                type="number"
                id="total_usage_limit"
                name="total_usage_limit"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.total_usage_limit}
                error={
                  formik.touched.total_usage_limit &&
                  Boolean(formik.errors.total_usage_limit)
                }
                helperText={
                  formik.touched.total_usage_limit &&
                  formik.errors.total_usage_limit
                }
                variant="outlined"
                fullWidth
                InputProps={inputProps}
                sx={textFieldStyle}
                placeholder="e.g. 100"
              />
            </Box>
            <Box sx={{ width: "100%" }}>
              <Box>
                Limit Per Customer <span style={{ color: "red" }}>*</span>
              </Box>
              <TextField
                type="number"
                id="limit_per_person"
                name="limit_per_person"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.limit_per_person}
                error={
                  formik.touched.limit_per_person &&
                  Boolean(formik.errors.limit_per_person)
                }
                helperText={
                  formik.touched.limit_per_person &&
                  formik.errors.limit_per_person
                }
                variant="outlined"
                fullWidth
                InputProps={inputProps}
                sx={textFieldStyle}
                placeholder="e.g. 1 (0 for unlimited)"
              />
            </Box>
          </Box>

          {/* Subscription Type */}
          <Box sx={{ width: "100%", mt: 2 }}>
            <Box>
              Applicable Subscription Type{" "}
              <span style={{ color: "red" }}>*</span>
            </Box>
            <FormControl
              fullWidth
              error={
                formik.touched.subscription_type &&
                Boolean(formik.errors.subscription_type)
              }
            >
              <Select
                value={formik.values.subscription_type}
                id="subscription_type"
                name="subscription_type"
                onChange={formik.handleChange}
                size="small"
                sx={{
                  margin: "0.5rem 0",
                  height: 55,
                  width: "100%",
                  padding: "4px 12px",
                  fontSize: "14px",
                  borderRadius: "12px",
                  "&:hover": {
                    backgroundColor: "#FFF3E8",
                    borderRadius: "12px",
                    color: "#F57C00",
                  },
                }}
              >
                {subScriptionType.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.subscription_type &&
                formik.errors.subscription_type && (
                  <FormHelperText>
                    {formik.errors.subscription_type}
                  </FormHelperText>
                )}
            </FormControl>
          </Box>

          {/* Buttons */}
          <Box
            sx={{
              width: "100%",
              mt: 3,
              display: "flex",
              gap: 2,
              justifyContent: "center",
            }}
          >
            <Button
              onClick={() => {
                handleClose();
                formik.resetForm();
                setModalData({});
              }}
              variant="contained"
              sx={{
                width: "100px",
                textTransform: "none",
                borderRadius: "8px",
                height: 45,
                border: "1px solid #164FA1",
                backgroundColor: "#FFF",
                color: "#164FA1",
                "&:hover": {
                  backgroundColor: "#FFF",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              onClick={formik.handleSubmit}
              variant="contained"
              disabled={formik.isSubmitting || loading}
              sx={{
                width: "100px",
                textTransform: "none",
                borderRadius: "8px",
                height: 45,
                backgroundColor: "#164FA1",
                "&:hover": {
                  backgroundColor: "#164FA1",
                },
              }}
            >
              {loading ? "Submitting..." : "Submit"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
}
